<template>
    <div>
        <slide-up-modal
            v-if="fileToDelete"
            ref="fileDeletionModal"
            modal-id="fileDeletionModal"
            modal-title="Suppression de fichier"
            @modal-closed="hideFileDeletionModal"
        >
            <p>
                Êtes-vous certain de vouloir supprimer le fichier 
                <strong>{{ fileToDelete.file_name }}</strong> ? <br>
                Cette action est irréversible.
            </p>

            <div class="actions" style="margin-top: 20px;">
                <button class="btn btn-complete btn-sm" @click="performFileDeletion">Valider</button>
                <button class="btn btn-default btn-sm" @click="hideFileDeletionModal" style="margin-left: 5px;">Annuler</button>
            </div>
        </slide-up-modal>
        
        <slide-up-modal
            v-if="showDocumentValidationModal"
            ref="documentValidationModal"
            modal-id="documentValidationModal"
            modal-title="Validation du document"
            @modal-closed="hideDocumentValidationModal"
        >
            <p>
                Vous vous apprêtez à valider le document <strong>{{ expectedDocumentName }}</strong>.
                <br>Assurez-vous d'avoir passé en revue les fichiers associés au préalable car cette opération est irréversible.
            </p>

            <div v-if="customerDocument.document_type.can_expire" style="margin-top: 20px;">
                <p style="font-weight: 600; margin-bottom: 5px;">Date d'expiration du document</p>
                <custom-date-picker 
                    :field="documentExpirationDateConfig" 
                    :value="documentExpirationDate" 
                    :field_idx="1" 
                    @field-updated="documentExpirationDate = $event.new_value"
                >
                </custom-date-picker>
            </div>

            <div class="actions" style="margin-top: 20px;">
                <button class="btn btn-complete btn-sm" @click="performDocumentValidation">Valider</button>
                <button class="btn btn-default btn-sm" @click="hideDocumentValidationModal" style="margin-left: 5px;">Annuler</button>
            </div>
        </slide-up-modal>

        <slide-up-modal
            v-if="showDocumentRefusalModal"
            ref="documentRefusalModal"
            modal-id="documentRefusalModal"
            modal-title="Validation du document"
            @modal-closed="hideDocumentRefusalModal"
        >
            <p>
                Vous vous apprêtez à refuser le document <strong>{{ expectedDocumentName }}</strong>.
                <!-- <br><strong>Attention : </strong>ceci aura également pour effet de supprimer tous les fichiers déjà envoyés. -->
            </p>

            <div class="actions" style="margin-top: 20px;">
                <button class="btn btn-complete btn-sm" @click="performDocumentRefusal">Valider</button>
                <button class="btn btn-default btn-sm" @click="hideDocumentRefusalModal" style="margin-left: 5px;">Annuler</button>
            </div>
        </slide-up-modal>

        <div v-if="!componentIsLoading">
            <div v-if="hasFiles" class="existing-version">
                <p class="title">Nouvelle version</p>
                
                <div class="subtitle">
                    <div class="text">
                        <p>En cours</p>
                        <p>Démarré le {{ customerDocument.created_at | moment-date-compact }}</p>
                    </div>

                    <button class="btn btn-complete btn-filled btn-sm" @click="$refs.file.click()">
                        <i class="fa fa-upload"></i>
                        <span>Ajouter une pièce</span>
                    </button>
                </div>

                <div class="files-list">
                    <draggable v-model="customerDocument.customer_document_files" @change="onFileOrderChange($event)">
                        <div v-for="(file, idx) in customerDocumentFiles" :key="'customer-document-file-' + idx" class="file-item">
                            <div>
                                <p class="index">Pièce n° {{ idx + 1 }}<span>, créé le {{ file.created_at | moment-date-compact }}</span></p>
        
                                <p class="name">{{ truncate(file.file_name, 50) }}</p>
                            </div>

                            <div class="buttons">
                                <button class="btn btn-sm" @click="displayFileDeletionModal(file)">
                                    <i class="fa fa-trash" style="margin: 0;"></i>
                                </button>
                                
                                <a :href="getFileStreamUrl(file)" target="_blank" class="btn btn-complete btn-sm" style="margin-left: 5px;">
                                    <i class="fa fa-eye"></i>
                                    <span>Voir</span>
                                </a>
                            </div>
                        </div>
                    </draggable>
                </div>

                <div v-if="canBeValidated" class="validation-container">
                    <button class="btn btn-complete btn-sm" @click="displayDocumentValidationModal()">
                        <span>Valider le document</span>
                    </button>
                    
                    <button class="btn btn-sm" v-if="'pending-validation' == customerDocument.status" @click="displayDocumentRefusalModal()">
                        <span>Refuser le document</span>
                    </button>
                </div>
            </div>
    
            <div v-else class="non-existing-version">
                <button class="btn btn-complete btn-filled" @click="$refs.file.click()">
                    Créer une nouvelle version
                </button>
            </div>
        </div>

        <component-loader v-else></component-loader>

        <input type="file" accept=".jpg, .png, .jpeg, .pdf" ref="file" class="hidden" @change="onFileInputChanged($event)">
    </div>
</template>

<script type="text/babel">
import {mapState, mapActions} from "vuex"
import api from 'store/api'
import SlideUpModal from 'components/Modals/SlideUpModal'
import CustomDatePicker from "components/Forms/Elements/Datepicker"
import Draggable from "vuedraggable"
import ComponentLoader from 'components/ComponentLoader'
import customerDocumentFilesMixin from 'mixins/customerDocumentFiles'

export default {
    name:"DocumentDraftVersionManager",
    
    props: {
        index: {
            type: Number,
            required: true
        },
        customerDocumentId: {
            type: Number,
            required: true
        },
        expectedDocumentName: {
            type: String,
            required: true
        }
    },

    mixins: [
        customerDocumentFilesMixin
    ],
    
    data () {
        return {
            componentIsLoading: false,
            customerDocument: null,
            fileToUpload: null,
            fileToDelete: null,
            showDocumentValidationModal: false,
            showDocumentRefusalModal: false,
            documentExpirationDate: null
        }
    },

    components: {
        SlideUpModal,
        CustomDatePicker,
        Draggable,
        ComponentLoader
    },

    mounted() {
        this.fetchCustomerDocument()
    },

    computed: {
        hasFiles() {
            return this.customerDocument != null && this.customerDocument.has_files
        },
        customerDocumentFiles() {
            return this.customerDocument != null ? this.customerDocument.customer_document_files : []
        },
        canBeValidated() {
            return this.customerDocument != null ? 'none' != this.customerDocument.document_type.versioning_type : false
        },
        documentExpirationDateConfig() {
            return {
                values: [], 
                multiple: false, 
                placeholder: "", 
                disabled: this.componentIsLoading, 
                idx: 1, 
                required: true, 
                value: this.documentExpirationDate, 
                label: "",
                type: "datepicker"
            }
        },
        orderedDocuments () {
            return _.sortBy(this.customerDocument.customer_document_files, (file) => file.order)
        }
    },

    methods: {
        ...mapActions("customers", ["updateCustomerDocumentFileOrder"]),

        fetchCustomerDocument() {
            this.componentIsLoading = true

            return api.getCustomerDocument(this.customerDocumentId, {with: 'document_type;customer_document_files'})
                .then((response) => {
                    this.customerDocument = response.data.customer_document
                    this.resetDocumentExpirationDate()
                    this.componentIsLoading = false
                })
        },
        resetDocumentExpirationDate() {
            this.documentExpirationDate = moment().add(1, 'years').format('YYYY-MM-DD')
        },
        onFileInputChanged(event) {
            this.fileToUpload = event.target.files[0]
        },
        uploadFile() {
            this.componentIsLoading = true

            let formData = new FormData()
            formData.append('document_file', this.fileToUpload, this.fileToUpload.name)

            return api.storeCustomerDocumentFile(this.customerDocumentId, formData)
                .then(response => {
                    this.$emit('file-uploaded', this.index)
                })
                .catch(error => {
                    this.addNotification("Une erreur s'est produite. Merci de réessayer plus tard", 'error')

                    this.componentIsLoading = false
                })
                .then(() => {
                    this.resetFileToUpload()
                })
        },
        resetFileToUpload() {
            this.$refs.file.value = ''
            this.fileToUpload = null
        },
        displayFileDeletionModal(file) {
            this.fileToDelete = file
        },
        hideFileDeletionModal() {
            this.fileToDelete = null
        },
        performFileDeletion() {
            if (this.fileToDelete) {
                this.$refs.fileDeletionModal.isLoading = true

                return api.deleteCustomerDocumentFile(this.fileToDelete.id)
                    .then(response => {
                        this.$emit('file-deleted', this.index)
                    })
                    .catch(() => {
                        this.hideFileDeletionModal()
                        this.addNotification("Une erreur s'est produite, merci de réessayer", 'error')
                    })
            }
        },
        displayDocumentValidationModal() {
            this.showDocumentValidationModal = true
        },
        hideDocumentValidationModal() {
            this.resetDocumentExpirationDate()
            this.showDocumentValidationModal = false
        },
        performDocumentValidation() {
            if (this.showDocumentValidationModal) {
                this.$refs.documentValidationModal.isLoading = true

                return api.validateCustomerDocument(this.customerDocument.id, {expired_at: moment(this.documentExpirationDate, "DD/MM/YYYY").format("YYYY-MM-DD")})
                    .then(response => {
                        this.$emit('document-validated', this.index)
                    })
                    .catch(() => {
                        this.hideDocumentValidationModal()
                        this.addNotification("Une erreur s'est produite, merci de réessayer", 'error')
                    })
            }
        },
        displayDocumentRefusalModal() {
            this.showDocumentRefusalModal = true
        },
        hideDocumentRefusalModal() {
            this.showDocumentRefusalModal = false
        },
        performDocumentRefusal() {
            if (this.showDocumentRefusalModal) {
                this.$refs.documentRefusalModal.isLoading = true

                return api.invalidateCustomerDocument(this.customerDocument.id)
                    .then(response => {
                        this.$emit('document-refused', this.index)
                    })
                    .catch(() => {
                        this.hideDocumentRefusalModal()
                        this.addNotification("Une erreur s'est produite, merci de réessayer", 'error')
                    })
            }
        },
        onFileOrderChange(event) {
            if (event) {
                this.updateCustomerDocumentFileOrder({
                    customer_document_file_id: event.moved.element.id,
                    params: {
                        order: this.orderedDocuments[event.moved.newIndex].order
                    }
                })
            }
        }
    },

    watch: {
        fileToUpload(newValue, oldValue) {
            if (newValue) {
                // Type validation
                const re = /(\.jpg|\.jpeg|\.png|\.pdf)$/i;

                if (!re.exec(this.fileToUpload.name)) {
                    this.resetFileToUpload()

                    this.addNotification('Type de fichier invalide : seuls les JPG, PNG et PDF sont acceptés', 'error')

                    return
                }

                // Size validation
                if (this.fileToUpload.size / 1024 / 1024 > 30) {
                    this.resetFileToUpload()

                    this.addNotification('Taille maximale de fichier dépassée', 'error')

                    return
                }

                this.uploadFile()
            }
        },
        customerDocumentId() {
            this.fetchCustomerDocument()
        }
    }
}
</script>

<style lang="scss" scoped>
    .existing-version {
        p {
            margin: 0;
            font-size: inherit;
        }
        .title {
            text-transform: uppercase;
            font-weight: 600;
            font-size: 16px;
        }
        .subtitle {
            margin-top: 10px;
            display: flex;
            justify-content: space-between;
            align-items: center;

            .text {
                font-size: 26px;

                p + p {
                    font-size: 13px;
                    margin-top: 5px;
                }
            }
        }

        .files-list {
            margin-top: 10px;

            .file-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                background-color: #fff;
                padding: 6px 8px;
                margin-bottom: 6px;
                border-radius: 3px;
                cursor: move;
                cursor: grab;

                &:active {
                    cursor: grabbing;
                }

                .index {
                    font-weight: 600;
                    font-size: 16px;
                    color: #555;

                    span {
                        font-size: 14px;
                        color: #888;
                    }
                }
                .name {
                    font-size: 14px;
                    color: #777;
                }
            }
        }

        .validation-container {
            margin-top: 30px;
            text-align: right;
        }

        button + button {
            margin-left: 5px;
        }
    }

    .non-existing-version {
        min-height: 200px;
        display: flex;
        justify-content: center;
        align-items: center;
    }
</style>


// WEBPACK FOOTER //
// client/components/CustomerDocuments/DraftVersionManager.vue